import { modelsv2 } from "algosdk";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DSContractListingMap } from "./types";
import { DSContractListing } from "@myth-finance/dualstake-ts-sdk";
import { network } from "@/config";
import { BenchStateSmall } from "@myth-finance/bench-sdk";

export const N = Number;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function shorten(str: string, n = 6, both = true): string {
  if (!str) return str;
  return str.slice(0, n) + ".." + (both ? str.slice(-n) : "");
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function stringify(x: any) {
  return JSON.stringify(
    x,
    (_, v) => (typeof v === "bigint" ? v.toString() : v),
    2
  );
}

export function plural(word: string, count: bigint | number) {
  return Number(count) === 1 ? word : word + "s";
}

export function formatNumber(
  v: number | BigInt,
  decimals: number = 6,
  precision: number = 2
) {
  v = Number(v);
  if (v === 0) return "0";
  return toLocaleString(v / 10 ** decimals, precision);
}

export function capitalizeFirst(str: string): string {
  if (!str) return "";
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
}

export function toPastTense(verb: string) {
  return verb.endsWith("e") ? verb + "d" : verb + "ed";
}

export function parseContractError(str: string): string | undefined {
  const re = /(err opcode executed|assert failed).*opcodes=.*\/\/ "([^"]+)"/m;
  const match = re.exec(str);
  if (match && match[2]) {
    if (match[2].includes("ERR")) {
      return match[2];
    }
  }
}

export function parseAlgodError(
  err: string
): { code: string; message?: string } | undefined {
  if (typeof err === "string" && err.includes("txn dead: round")) {
    return { code: "ERR:TXN EXPIRED" };
  }
  const algoBalanceErrRegex =
    /: account ([A-Z2-7]{58}) balance ([0-9]+) below min ([0-9]+)/;
  if (algoBalanceErrRegex.test(err)) {
    const [_, acct, endBalance, minBalance] =
      algoBalanceErrRegex.exec(err) ?? [];
    const diff = Number(minBalance) - Number(endBalance);
    return {
      code: `ERR:OVERSPEND`,
      message: `Not enough ALGO balance in account ${shorten(
        acct
      )}, need ${formatNumber(diff, 6, 6)} more ALGO`,
    };
  }
  const networkRegex = /Network mismatch error|invalid [a-zA-Z_0-9-]+ genesis/;
  if (networkRegex.test(err)) {
    return {
      code: "ERR:NETWORK",
      message: `Incorrect network setting in wallet. Make sure your wallet is set to ${capitalizeFirst(network)}.`,
    }
  }
}

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

export function showTimeDiffPartial(
  difference: number,
  labelsToShow = 2,
  small = false
): string {
  if (difference === 0) return "Instant"
  const labels = small
    ? ["d", "H", "m", "s"]
    : [" days", " hours", " mins", " sec"];
  const values = [
    Math.floor(difference / DAY),
    Math.floor((difference % DAY) / HOUR),
    Math.floor((difference % HOUR) / MINUTE),
    Math.floor((difference % MINUTE) / SECOND),
  ];
  let included = 0;
  let s = "";
  for (let i = 0; i < labels.length && included < labelsToShow; i++) {
    const label = labels[i];
    const value = values[i];
    if (value) {
      included++;
      const formattedLabel =
        value === 1 && label.endsWith("s") ? label.slice(0, -1) : label;
      s += `${value}${formattedLabel} `;
    }
  }
  return s.trim();
}

type BenchOpenState = "upcoming" | "open" | "closed";

export function calculateBenchState(benchData: BenchStateSmall): BenchOpenState {
  const now = dateNow()
  const { timeStart, timeEnd } = benchData;
  if (timeStart && timeStart > now) return "upcoming";
  if ((timeEnd && timeEnd < now) || benchData?.remainingTokens === 0) {
    return "closed";
  }
  return "open";
}

export function dateNow(): number {
  return Math.floor(Date.now() / 1000);
}

export function base64ToUint8Array(b64: string): Uint8Array {
  return new Uint8Array(Buffer.from(b64, "base64"));
}

export function toLocaleString(num: number, maximumFractionDigits = 2) {
  if (num === undefined || num === null) return "";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maximumFractionDigits,
  });
}

export function toFixed(num: number, digits: number) {
  if (num === undefined || num === null) return "";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function shortenAmount(num: number, decimals: number): string {
  return shortenNumber(num / (10 ** decimals))
}

export function shortenNumber(num: number): string {
  const suffixes = ["", "K", "M", "B"];
  const tier = Math.floor(Math.log10(Math.abs(num)) / 3);

  if (num <= 0) return String(num);
  if (tier < 0)
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    });
  if (tier === 0)
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const suffix = suffixes[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = num / scale;

  const rounded = scaled % 1 === 0 ? scaled.toFixed(0) : scaled.toFixed(1);

  return `${rounded}${suffix}`;
}

export function findListingByAsaID(
  listings: DSContractListingMap,
  asaId: bigint
): DSContractListing | undefined {
  if (!listings?.size) return;
  for (const listing of listings.values())
    if (listing.asaId === asaId) return listing;
}

export function findListingByLstName(
  listings: DSContractListingMap | DSContractListing[],
  lstName: string
): DSContractListing | undefined {
  if (Array.isArray(listings)) {
    if (!listings.length) return;
  } else {
    if (!listings?.size) return;
  }
  for (const listing of listings.values())
    if (listing.lstName === lstName) return listing;
}

export function findListingByLstId(
  listings: DSContractListingMap,
  lstId: bigint
): DSContractListing | undefined {
  if (!listings?.size) return;
  for (const listing of listings.values())
    if (listing.lstId === lstId) return listing;
}

export function max(a: bigint, b: bigint): bigint {
  return a > b ? a : b;
}

export const preventDefault = (e: any) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

export const aOrAn = (word: string) => (/^[aeoiu]/ig.test(word) ? "an" : "a");

export const getBalanceFromAccountInfo = (
  assetId: number | bigint,
  accountInformation: modelsv2.Account
) => {
  const existing = accountInformation.assets?.find(
    ({ assetId: aid }) => aid === assetId
  );
  if (existing) {
    return Number(existing.amount);
  } else {
    return -1;
  }
};
